// Concussion Template Configuration
// Questions simplified to 12-year-old reading level

export const concussionTemplate = {
  id: 'CONCUSSION',
  name: 'Concussion / Head Injury',
  icon: '🧠',
  dominantSide: 'hand',

  fields: {
    injuryDate: {
      type: 'date',
      label: 'When did the head injury happen?',
      required: true
    },

    injurySetting: {
      type: 'radio',
      label: 'How did it happen?',
      options: [
        'Playing sport',
        'Car accident',
        'Fall',
        'Hit by someone/something',
        'Other'
      ],
      required: true
    },

    injurySettingOther: {
      type: 'text',
      label: 'Please describe:',
      showWhen: { field: 'injurySetting', equals: 'Other' }
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Tell us exactly what happened:',
      placeholder: 'Describe the accident or injury in detail',
      rows: 3,
      required: true
    },

    impactLocation: {
      type: 'checkbox-multiple',
      label: 'Where did you hit your head?',
      options: [
        'Front of head',
        'Back of head',
        'Side of head (right)',
        'Side of head (left)',
        'Top of head',
        'Chin/jaw',
        'I didn\'t hit my head directly (whiplash)'
      ]
    },

    woreHelmet: {
      type: 'radio',
      label: 'Were you wearing a helmet?',
      options: ['Yes', 'No', 'Not applicable']
    },

    // Loss of Consciousness
    lostConsciousness: {
      type: 'radio',
      label: 'Did you black out (lose consciousness)?',
      options: ['Yes', 'No', 'Not sure']
    },

    unconsciousDuration: {
      type: 'radio',
      label: 'How long were you knocked out?',
      options: ['Just a few seconds', 'Less than a minute', '1-5 minutes', 'More than 5 minutes', 'Not sure'],
      showWhen: { field: 'lostConsciousness', equals: 'Yes' }
    },

    // Memory Loss
    memoryBefore: {
      type: 'radio',
      label: 'Do you have trouble remembering what happened BEFORE the injury?',
      options: ['Yes', 'No']
    },

    memoryBeforeDuration: {
      type: 'text',
      label: 'How much time before the injury can\'t you remember?',
      placeholder: 'e.g., 5 minutes, 1 hour',
      showWhen: { field: 'memoryBefore', equals: 'Yes' }
    },

    memoryAfter: {
      type: 'radio',
      label: 'Do you have trouble remembering what happened AFTER the injury?',
      options: ['Yes', 'No']
    },

    memoryAfterDuration: {
      type: 'text',
      label: 'How much time after the injury can\'t you remember?',
      placeholder: 'e.g., 10 minutes, 2 hours',
      showWhen: { field: 'memoryAfter', equals: 'Yes' }
    },

    hadSeizure: {
      type: 'radio',
      label: 'Did you have a seizure (fit/convulsion)?',
      options: ['Yes', 'No', 'Not sure']
    },

    behaviourAfter: {
      type: 'checkbox-multiple',
      label: 'How did you feel/act right after the injury?',
      options: [
        'Dazed or confused',
        'Unsteady on my feet',
        'Emotional or upset',
        'Quiet or withdrawn',
        'Seemed normal',
        'Other'
      ]
    },

    // Current Physical Symptoms
    currentHeadache: {
      type: 'radio',
      label: 'Do you have a headache?',
      options: ['Yes', 'No']
    },

    headacheSeverity: {
      type: 'radio',
      label: 'How bad is your headache? (0 = none, 10 = worst ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
      showWhen: { field: 'currentHeadache', equals: 'Yes' }
    },

    physicalSymptoms: {
      type: 'checkbox-multiple',
      label: 'What physical symptoms are you having? (tick all that apply)',
      options: [
        'Headache',
        'Dizziness',
        'Feeling sick (nausea)',
        'Vomiting',
        'Balance problems',
        'Blurry or double vision',
        'Sensitive to light',
        'Sensitive to noise',
        'Very tired',
        'Neck pain',
        'None of these'
      ]
    },

    // Cognitive Symptoms
    cognitiveSymptoms: {
      type: 'checkbox-multiple',
      label: 'Are you having any thinking problems? (tick all that apply)',
      options: [
        'Feeling confused',
        'Hard to concentrate',
        'Memory problems',
        'Thinking feels slow',
        'Feeling "foggy" or "in a daze"',
        'None of these'
      ]
    },

    // Emotional Symptoms
    emotionalSymptoms: {
      type: 'checkbox-multiple',
      label: 'Are you having any emotional changes? (tick all that apply)',
      options: [
        'More irritable than usual',
        'Feeling sad',
        'More anxious or nervous',
        'Mood swings',
        'None of these'
      ]
    },

    // Sleep Symptoms
    sleepSymptoms: {
      type: 'checkbox-multiple',
      label: 'Has your sleep changed? (tick all that apply)',
      options: [
        'More drowsy than usual',
        'Trouble falling asleep',
        'Sleeping more than usual',
        'Sleeping less than usual',
        'No changes to sleep'
      ]
    },

    // RED FLAG SYMPTOMS - EMERGENCY
    redFlagHeadache: {
      type: 'radio',
      label: 'Is your headache getting worse over time?',
      options: ['Yes', 'No']
    },

    redFlagVomiting: {
      type: 'radio',
      label: 'Have you vomited more than once?',
      options: ['Yes', 'No']
    },

    redFlagSeizure: {
      type: 'radio',
      label: 'Have you had any seizures (fits) since the injury?',
      options: ['Yes', 'No']
    },

    redFlagSpeech: {
      type: 'radio',
      label: 'Is your speech slurred or hard to understand?',
      options: ['Yes', 'No']
    },

    redFlagPupils: {
      type: 'radio',
      label: 'Do your pupils (the black part of your eyes) look different sizes?',
      options: ['Yes', 'No', 'Not sure']
    },

    redFlagDrowsiness: {
      type: 'radio',
      label: 'Are you getting more and more drowsy or hard to wake up?',
      options: ['Yes', 'No']
    },

    redFlagWeakness: {
      type: 'radio',
      label: 'Do you have new weakness or numbness in your arms or legs?',
      options: ['Yes', 'No']
    },

    redFlagNeckPain: {
      type: 'radio',
      label: 'Do you have bad neck pain?',
      options: ['Yes', 'No']
    },

    redFlagFluid: {
      type: 'radio',
      label: 'Is there clear fluid coming from your nose or ears?',
      options: ['Yes', 'No']
    },

    // Previous History
    previousConcussions: {
      type: 'radio',
      label: 'Have you ever had a concussion or head injury before?',
      options: ['Yes', 'No']
    },

    previousConcussionCount: {
      type: 'radio',
      label: 'How many previous concussions have you had?',
      options: ['1', '2', '3', '4 or more'],
      showWhen: { field: 'previousConcussions', equals: 'Yes' }
    },

    previousConcussionRecovery: {
      type: 'radio',
      label: 'Did you fully recover from your previous concussion(s)?',
      options: ['Yes', 'No', 'Not sure'],
      showWhen: { field: 'previousConcussions', equals: 'Yes' }
    },

    timeSinceLastConcussion: {
      type: 'text',
      label: 'How long ago was your last concussion?',
      placeholder: 'e.g., 6 months, 2 years',
      showWhen: { field: 'previousConcussions', equals: 'Yes' }
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Migraines or bad headaches',
        'Epilepsy or seizures',
        'ADHD',
        'Depression or anxiety',
        'Learning difficulties',
        'None of these'
      ]
    },

    bloodThinners: {
      type: 'radio',
      label: 'Do you take blood thinning medication (like warfarin or aspirin)?',
      options: ['Yes', 'No', 'Not sure']
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies'
    },

    // Sport/Activity History
    sport: {
      type: 'text',
      label: 'What sport were you playing (if it happened during sport)?',
      placeholder: 'e.g., rugby, football, netball'
    },

    sportLevel: {
      type: 'radio',
      label: 'What level do you play at?',
      options: ['Just for fun', 'Club/competitive', 'Professional/elite', 'Not applicable']
    },

    triedToReturn: {
      type: 'radio',
      label: 'Did you try to keep playing or go back to normal activities?',
      options: ['Yes', 'No']
    },

    // Current Status
    symptomsProgress: {
      type: 'radio',
      label: 'Overall, are your symptoms:',
      options: ['Getting better', 'Getting worse', 'Staying the same']
    },

    hadMedicalCare: {
      type: 'radio',
      label: 'Have you seen a doctor or been to hospital since this happened?',
      options: ['Yes', 'No']
    },

    hadImaging: {
      type: 'radio',
      label: 'Have you had any scans (CT or MRI)?',
      options: ['Yes - CT scan', 'Yes - MRI scan', 'Yes - both', 'No']
    },

    imagingResults: {
      type: 'text',
      label: 'What did the scan show?',
      placeholder: 'If you know the results',
      showWhen: { field: 'hadImaging', includes: 'Yes' }
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. About The Injury',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injurySetting', 'injurySettingOther', 'injuryDescription', 'impactLocation', 'woreHelmet']
    },
    {
      id: 'immediate',
      title: '3. What Happened Right After',
      component: 'GenericSection',
      enabled: true,
      fields: ['lostConsciousness', 'unconsciousDuration', 'memoryBefore', 'memoryBeforeDuration', 'memoryAfter', 'memoryAfterDuration', 'hadSeizure', 'behaviourAfter']
    },
    {
      id: 'currentSymptoms',
      title: '4. Your Current Symptoms',
      component: 'GenericSection',
      enabled: true,
      fields: ['currentHeadache', 'headacheSeverity', 'physicalSymptoms', 'cognitiveSymptoms', 'emotionalSymptoms', 'sleepSymptoms']
    },
    {
      id: 'redFlags',
      title: '5. Important Safety Questions',
      component: 'RedFlagsSection',
      enabled: true,
      fields: [
        { id: 'redFlagHeadache', label: 'Worsening headache', weight: 8, urgent: true },
        { id: 'redFlagVomiting', label: 'Repeated vomiting', weight: 8, urgent: true },
        { id: 'redFlagSeizure', label: 'Seizures', weight: 10, urgent: true },
        { id: 'redFlagSpeech', label: 'Slurred speech', weight: 8, urgent: true },
        { id: 'redFlagPupils', label: 'Unequal pupils', weight: 10, urgent: true },
        { id: 'redFlagDrowsiness', label: 'Progressive drowsiness', weight: 9, urgent: true },
        { id: 'redFlagWeakness', label: 'Weakness or numbness', weight: 8, urgent: true },
        { id: 'redFlagNeckPain', label: 'Severe neck pain', weight: 7 },
        { id: 'redFlagFluid', label: 'Clear fluid from nose/ears', weight: 10, urgent: true }
      ],
      alerts: {
        critical: { threshold: 8, message: 'EMERGENCY: Go to the Emergency Department immediately' },
        urgent: { threshold: 5, message: 'Please seek urgent medical attention today' }
      }
    },
    {
      id: 'history',
      title: '6. Previous Head Injuries',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousConcussions', 'previousConcussionCount', 'previousConcussionRecovery', 'timeSinceLastConcussion']
    },
    {
      id: 'medical',
      title: '7. Your Health',
      component: 'MedicalHistorySection',
      enabled: true
    },
    {
      id: 'activity',
      title: '8. Sport & Activity',
      component: 'GenericSection',
      enabled: true,
      fields: ['sport', 'sportLevel', 'triedToReturn']
    },
    {
      id: 'current',
      title: '9. Current Status',
      component: 'GenericSection',
      enabled: true,
      fields: ['symptomsProgress', 'hadMedicalCare', 'hadImaging', 'imagingResults']
    },    {
      id: 'exerciseVitalSign',
      title: '10. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '11. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Playing sport': 'sporting injury',
      'Car accident': 'motor vehicle accident',
      'Fall': 'fall',
      'Hit by someone/something': 'direct blow'
    },
    commonDiagnoses: [
      'Concussion',
      'Mild traumatic brain injury',
      'Post-concussion syndrome'
    ]
  }
};

export default concussionTemplate;
